import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";

import CardHeader from "../General/CardHeader";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import GlassCard from "../General/GlassCard";
import NotificationCertificateAddressPreviewModal from "./NotificationCertificateAddressPreviewModal";
import { useToast } from "../../context/ToastContext";
import FileUploadBox from "../General/FileUploadBox"; 

import {
  previewGenerateNotificationCertificates,
  uploadGenerateNotificationCertificates,
} from "../../services/NotificationCertificateService";

export default function ChargeGenerateNotificationCertificate() {
  const { showToast } = useToast();
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [form, setForm] = useState({ file: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);

  const [stage, setStage] = useState("stage_1");

  const [openHelp, setOpenHelp] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);

  const [previewItems, setPreviewItems] = useState([]);

  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const resetForm = () => {
    setForm({ file: "" });
    setSelectedFile(null);
    setErrors({});
    setPreviewItems([]);
    setOpenPreviewModal(false);
    setStage("stage_1");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClosePreview = () => {
    setOpenPreviewModal(false);
    setPreviewItems([]);
  };

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          file: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, file: "" });
        return;
      }

      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
      setForm({ ...form, file: file.name });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrors({});
    setPreviewItems([]);

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("stage", stage);

    try {
      setPreviewLoading(true);

      const response = await previewGenerateNotificationCertificates(formData);
      const items = response.data?.data || [];

      if (!items.length) {
        showToast(
          "No se encontraron registros válidos para previsualizar.",
        "error")
        return;
      }

      setPreviewItems(items);
      setOpenPreviewModal(true);
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error")
      } else {
        console.error(error);
        showToast("Ocurrió un error inesperado al previsualizar.", "error")
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmGenerate = async ({ items, templateHtml }) => {
    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("items", JSON.stringify(items || previewItems));
      formData.append("template_html", templateHtml || "");
      formData.append("stage", stage);

      const response = await uploadGenerateNotificationCertificates(formData);

      showToast(response.data.message, "success")

      resetForm();
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error")
      } else {
        console.error(error);
        showToast("Ocurrió un error inesperado al generar cédulas.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Generador de Cédulas de Notificación"
            description="Sube un archivo Excel, revisa domicilios, ajusta la plantilla y luego genera las cédulas."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12}}>
                <FormControl fullWidth>
                  <InputLabel>ETAPA</InputLabel>
                  <Select
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    label="Etapa"
                    disabled={previewLoading || loading}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="stage_1">ETAPA 1</MenuItem>
                    <MenuItem value="stage_2">ETAPA 2</MenuItem>
                    <MenuItem value="stage_3">ETAPA 3</MenuItem>
                    <MenuItem value="stage_4">ETAPA 4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12}}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel"
                  helperText="O arrastrá el Excel aquí"
                />

                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={previewLoading || loading}
                  sx={{ fontWeight: "bold", borderRadius: 2 }}
                >
                  {previewLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Previsualizar y editar"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <NotificationCertificateAddressPreviewModal
        open={openPreviewModal}
        onClose={handleClosePreview}
        items={previewItems}
        setItems={setPreviewItems}
        onConfirm={handleConfirmGenerate}
        loading={loading}
      />

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={[
          "Código Expediente",
          "Código Operación Principal",
          "Código Operación Secundaria",
        ]}
        example={["11111111", "12345678", "87654321"]}
      />
    </Grid>
  );
}
